import { useState, useEffect } from "react";
import {
  MapPin,
  Calendar,
  Clock,
  TrendingUp,
  Activity,
  Users,
  Car,
  Briefcase,
  FileText,
  ChevronRight,
  ChevronDown,
  ArrowRight,
  Filter,
  Search,
  RefreshCw,
  CalendarDays,
  Map,
  Zap,
} from "lucide-react";
import { GET_TIMELINE, MONTHLY_BDM_ATTENDANCE_V2 } from "../../constants";
import { useLocation, useNavigate } from "react-router-dom";
import { Dialog, DialogContent, IconButton, Tooltip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimelineIcon from "@mui/icons-material/Timeline";
import TimelineDialog from "../reusable Component/TimelineDialog";
import axios from "axios";

// Activity type configuration with colors and icons
const activityConfig = {
  "Attendance In": {
    color: "bg-blue-100 text-blue-800 border-blue-200",
    icon: <Clock className="h-4 w-4" />,
  },
  "Attendance Out": {
    color: "bg-blue-200 text-blue-900 border-blue-300",
    icon: <Clock className="h-4 w-4" />,
  },
  Travel: {
    color: "bg-green-100 text-green-800 border-green-200",
    icon: <Car className="h-4 w-4" />,
  },
  "HO Visit": {
    color: "bg-purple-100 text-purple-800 border-purple-200",
    icon: <Briefcase className="h-4 w-4" />,
  },
  Meeting_Field: {
    color: "bg-yellow-100 text-yellow-800 border-yellow-200",
    icon: <Users className="h-4 w-4" />,
  },
  "RO Visit": {
    color: "bg-indigo-100 text-indigo-800 border-indigo-200",
    icon: <FileText className="h-4 w-4" />,
  },
  "BO Visit": {
    color: "bg-pink-100 text-pink-800 border-pink-200",
    icon: <FileText className="h-4 w-4" />,
  },
  "Expo Event": {
    color: "bg-orange-100 text-orange-800 border-orange-200",
    icon: <Users className="h-4 w-4" />,
  },
  "Group Meeting": {
    color: "bg-teal-100 text-teal-800 border-teal-200",
    icon: <Users className="h-4 w-4" />,
  },
  "Site Visit": {
    color: "bg-amber-100 text-amber-800 border-amber-200",
    icon: <MapPin className="h-4 w-4" />,
  },
};

// Helper function to format date and time
const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr || typeof dateTimeStr !== "string") return "-";

  const parts = dateTimeStr.split(" ");
  if (parts.length !== 2) return dateTimeStr;

  const [datePart, timePart] = parts;
  return `${datePart} ${timePart}`;
};

// Helper function to format distance
const formatDistance = (distance) => {
  if (distance === null || distance === undefined || isNaN(distance))
    return "-";
  return `${parseFloat(distance).toFixed(2)} km`;
};

// Helper function to get activity config
const getActivityConfig = (action) => {
  return (
    activityConfig[action] || {
      color: "bg-gray-100 text-gray-800 border-gray-200",
      icon: <Activity className="h-4 w-4" />,
    }
  );
};

// Helper function to calculate total travel time in minutes
const calculateTotalTravelTime = (travelDetails) => {
  if (!travelDetails || !Array.isArray(travelDetails)) return 0;

  return travelDetails.reduce((total, detail) => {
    if (!detail || !detail.travelInfo || !detail.travelInfo.duration)
      return total;
    return total + (detail.travelInfo.duration.minutes || 0);
  }, 0);
};

// Helper function to format minutes to hours and minutes
const formatMinutes = (totalMinutes) => {
  if (
    totalMinutes === null ||
    totalMinutes === undefined ||
    isNaN(totalMinutes)
  ) {
    return "0h 0m";
  }

  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes}m`;
  } else if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}m`;
};

// Helper function to count activity types
const countActivities = (travelDetails) => {
  if (!travelDetails || !Array.isArray(travelDetails)) return {};

  return travelDetails.reduce((acc, detail) => {
    if (!detail || !detail.travelInfo) return acc;

    const action = detail.travelInfo.action || "Unknown";
    acc[action] = (acc[action] || 0) + 1;
    return acc;
  }, {});
};

// Safe data access helper
const getSafeData = (data, path, defaultValue = null) => {
  if (!data) return defaultValue;

  const keys = path.split(".");
  let result = data;

  for (const key of keys) {
    if (result === null || result === undefined) return defaultValue;
    result = result[key];
  }

  return result !== undefined ? result : defaultValue;
};

// Activity badge component
const ActivityBadge = ({ type, count = null }) => {
  const config = getActivityConfig(type);

  return (
    <span
      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color} border`}
    >
      <span className="mr-1">{config.icon}</span>
      {type}
      {count !== null ? `: ${count}` : ""}
    </span>
  );
};

// Stat card component
const StatCard = ({ icon, title, subtitle, value, color = "blue" }) => {
  const colorClasses = {
    blue: "text-blue-600",
    red: "text-red-600",
    green: "text-green-600",
    purple: "text-purple-600",
  };

  return (
    <div className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-center mb-2">
        <div className={`${colorClasses[color]} mr-3`}>{icon}</div>
        <h3 className="font-semibold text-gray-700">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-1">{subtitle}</p>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
    </div>
  );
};

// Timeline point component
const TimelinePoint = ({
  detail,
  setOpenImageDialog,
  setSelectedImage,
  handletimelineClick,
}) => {
  if (!detail || !detail.travelInfo) return null;

  const action = getSafeData(detail, "travelInfo.action", "Unknown");
  const checkinTime = getSafeData(detail, "travelInfo.checkin.time");
  const checkoutTime = getSafeData(detail, "travelInfo.checkout.time");
  const fromLastPoint = getSafeData(
    detail,
    "travelInfo.distances.fromLastPoint",
    0
  );
  const checkinToCheckout = getSafeData(
    detail,
    "travelInfo.distances.checkinToCheckout"
  );
  const durationFormatted = getSafeData(
    detail,
    "travelInfo.duration.formatted",
    "-"
  );
  const leadInfo = getSafeData(detail, "leadInfo");
  const customerName = getSafeData(detail, "leadInfo.customerName", "");
  const travelFormDetails = getSafeData(detail, "travelFormDetails");
  const config = getActivityConfig(action);

  return (
    <div className="mb-6 last:mb-0">
      <div className="flex flex-col md:flex-row gap-2">
        {/* Time column */}
        <div className="w-full md:w-36 text-sm">
          <div className="font-semibold text-gray-800">
            {checkinTime ? formatDateTime(checkinTime).split(" ")[1] : "--:--"}
          </div>
          {checkoutTime && (
            <div className="text-gray-800 font-semibold text-xs mt-1">
              To {formatDateTime(checkoutTime).split(" ")[1]}
              <span className="ml-1 text-gray-800 font-semibold">
                ({durationFormatted})
              </span>
            </div>
          )}
        </div>

        {/* Content column */}
        <div className="flex-1">
          <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm hover:shadow transition-shadow">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1">
              {/* Badge on top in mobile, right in larger screens */}
              <div className="mb-2 sm:mb-0 sm:order-2">
                <ActivityBadge type={action} />
              </div>

              {/* Distance details below badge in mobile, left in larger screens */}
              <div className="sm:order-1">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-2">
                  <div>
                    <div className="text-xs text-gray-500">
                      Distance from last
                    </div>
                    <div className="font-medium">
                      {formatDistance(fromLastPoint)}
                    </div>
                  </div>

                  {checkinToCheckout !== null &&
                    checkinToCheckout !== undefined && (
                      <div>
                        <div className="text-xs text-gray-500">
                          During activity
                        </div>
                        <div className="font-medium">
                          {formatDistance(checkinToCheckout)}
                        </div>
                      </div>
                    )}

                  <div>
                    <div className="text-xs text-gray-500">Check In</div>
                    <div className="font-medium">
                      {checkinTime
                        ? formatDateTime(checkinTime).split(" ")[1]
                        : "--:--"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs text-gray-500">Check Out</div>
                    <div className="font-medium">
                      {checkoutTime
                        ? formatDateTime(checkoutTime).split(" ")[1]
                        : "--:--"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Stats */}
            {/* <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-gray-500">Distance from last</div>
                <div className="font-medium">{formatDistance(fromLastPoint)}</div>
              </div>
              
              {checkinToCheckout !== null && checkinToCheckout !== undefined && (
                <div>
                  <div className="text-xs text-gray-500">During activity</div>
                  <div className="font-medium">{formatDistance(checkinToCheckout)}</div>
                </div>
              )}
            </div> */}

            {/* Details */}
            {travelFormDetails ? (
              <div className="mt-1 pt-3 border-t border-gray-100">
                <h5 className="text-sm font-medium text-gray-700 mb-2">
                  <strong>Activity Details</strong>
                </h5>
                <div className="grid grid-cols-1 gap-y-2 text-sm">
                  {travelFormDetails.taskType === "Travel" && (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          From:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.travelFrom || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          To:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.travelTo || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Mode:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.modeOfTravel || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Reason:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.reasonForTravel || "N/A"}
                        </span>
                      </div>
                    </>
                  )}

                  {travelFormDetails.taskType === "RO Visit" && (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          RO:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.regionalOfficeName || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Purpose:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.purposeForVisit || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Contact:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.concernPersonName || "N/A"}
                        </span>
                      </div>
                    </>
                  )}

                  {travelFormDetails.taskType === "HO Visit" && (
                    <div className="flex flex-row justify-between items-center">
                      <span className="text-gray-500 font-medium w-24">
                        Purpose:
                      </span>
                      <span className="break-words ml-auto">
                        {travelFormDetails.hoSelection || "N/A"}
                      </span>
                    </div>
                  )}

                  {travelFormDetails.taskType === "BO Visit" && (
                    <>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Branch:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.branchName || "N/A"}
                        </span>
                      </div>
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-24">
                          Purpose:
                        </span>
                        <span className="break-words ml-auto">
                          {travelFormDetails.purposeForVisit || "N/A"}
                        </span>
                      </div>
                      {travelFormDetails.adminTaskSelect && (
                        <div className="flex flex-row justify-between items-center">
                          <span className="text-gray-500 font-medium w-24">
                            Admin Task:
                          </span>
                          <span className="break-words ml-auto">
                            {travelFormDetails.adminTaskSelect}
                          </span>
                        </div>
                      )}
                    </>
                  )}

                  {travelFormDetails.remarks && (
                    <div className="flex flex-row justify-between items-center">
                      <span className="text-gray-500 font-medium w-24">
                        Remarks:
                      </span>
                      <span className="break-words ml-auto">
                        {travelFormDetails.remarks}
                      </span>
                    </div>
                  )}
                </div>

                {travelFormDetails.mandatoryVisitImage && (
                  <div className="mt-3">
                    <button
                      onClick={() => {
                        setSelectedImage(travelFormDetails.mandatoryVisitImage);
                        setOpenImageDialog(true);
                      }}
                      className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                    >
                      View image <ArrowRight className="h-3 w-3 ml-1" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              leadInfo && (
                <div className="mt-1 pt-3 border-t border-gray-100">
                  <h5 className="text-sm font-medium text-gray-700 mb-1">
                    <strong>Lead Information</strong>
                  </h5>
                  <div className="grid grid-cols-1 gap-y-2 text-sm">
                    <div className="flex flex-row justify-between items-center">
                      <span className="text-gray-500 font-medium w-40">
                        Customer Name:
                      </span>
                      <span className="text-gray-600 ml-auto">
                        {customerName || "N/A"}
                      </span>
                    </div>
                    <div className="flex flex-row justify-between items-center">
                      <span className="text-gray-500 font-medium w-40">
                        Category:
                      </span>
                      <span className="break-words ml-auto text-capitalize">
                        {leadInfo.category || "N/A"}
                      </span>
                    </div>
                    <div className="flex flex-row justify-between items-center">
                      <span className="text-gray-500 font-medium w-40">
                        Sub-category:
                      </span>
                      <span className="break-words ml-auto">
                        {leadInfo.subCategory || "N/A"}
                      </span>
                    </div>

                    {leadInfo.closeMonth && (
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-40">
                          Close Month:
                        </span>
                        <span className="break-words ml-auto">
                          {leadInfo.closeMonth}
                        </span>
                      </div>
                    )}

                    {leadInfo.address && (
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-40">
                          Address:
                        </span>
                        <span className="break-words ml-auto">
                          {leadInfo.address}
                        </span>
                      </div>
                    )}

                    {leadInfo.bdmRemark && (
                      <div className="flex flex-row justify-between items-center">
                        <span className="text-gray-500 font-medium w-40">
                          Remark:
                        </span>
                        <span className="break-words ml-auto">
                          {leadInfo.bdmRemark}
                        </span>
                      </div>
                    )}

                    <div className="mt-3">
                      <button
                        onClick={() => {
                          console.log("Timeline button clicked");
                          handletimelineClick(leadInfo);
                        }}
                        className="inline-flex items-center px-3 py-1.5 bg-blue-50 text-blue-700 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
                      >
                        <TimelineIcon className="h-3 w-3 mr-1" />
                        <span>Timeline</span>
                      </button>
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Day timeline component
const DayTimeline = ({
  day,
  isExpanded,
  toggleExpand,
  setOpenImageDialog,
  setSelectedImage,
  handletimelineClick,
  navigate,
  bdmId,
  bdmName,
}) => {
  if (!day || !day.travelDetails) return null;

  const activitiesCount = countActivities(day.travelDetails);
  const totalTravelTime = calculateTotalTravelTime(day.travelDetails);

  const handleMapClick = (day) => {
    navigate("/attendanceReport/details/map", {
      state: {
        prevFilters: day.date,
        bdmId: bdmId,
        bdmName: bdmName,
      },
    });
  };

  return (
    <div className="mb-3 bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Day header */}
      <div
        className="p-4 bg-gray-50 flex justify-between cursor-pointer hover:bg-gray-100 transition-colors"
        onClick={toggleExpand}
      >
        <div className="flex items-center">
          <div className="w-10 h-10 flex items-center justify-center bg-blue-100 rounded-lg mr-3">
            <CalendarDays className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">
              {day.formattedDate || "Unknown Date"}
            </h3>
            <div className="flex items-center text-sm text-gray-600 mt-1">
              <MapPin className="h-4 w-4 mr-1" />
              <span>{day.totalLocations || 0} locations</span>
              <span className="mx-2">•</span>
              <Car className="h-4 w-4 mr-1" />
              <span>{(day.totalDistanceForDay || 0).toFixed(2)} km</span>
            </div>
          </div>
        </div>

        <div className="flex items-center">
          <div className="hidden md:flex space-x-2 mr-4">
            {Object.entries(activitiesCount)
              .slice(0, 3) // Show only first 3 activities on larger screens
              .map(([activity, count]) => (
                <ActivityBadge key={activity} type={activity} count={count} />
              ))}

            {/* Show +X more if there are more than 3 activity types */}
            {Object.keys(activitiesCount).length > 3 && (
              <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full">
                +{Object.keys(activitiesCount).length - 3} more
              </span>
            )}
          </div>

          {totalTravelTime > 0 && (
            <span className="hidden lg:flex items-center text-sm text-gray-600 mr-4">
              <Clock className="h-4 w-4 mr-1" />
              {formatMinutes(totalTravelTime)}
            </span>
          )}

          {/* Map icon */}
          <Tooltip title="View Map" arrow>
            <IconButton
              style={{
                border: "1px solid",
                borderRadius: "4px",
                padding: "2px",
                width: "28px",
                height: "28px",
                backgroundColor: "#fff",
              }}
              color="primary"
              onClick={() => handleMapClick(day)}
            >
              <Map className="w-4 h-4" />
            </IconButton>
          </Tooltip>

          <div className="text-gray-400">
            {isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </div>
        </div>
      </div>

      {/* Day details (expanded) */}
      {isExpanded && (
        <div className="p-3">
          {/* Activity counts for mobile */}
          <div className="flex flex-wrap gap-2 mb-4 md:hidden">
            {Object.entries(activitiesCount).map(([activity, count]) => (
              <ActivityBadge key={activity} type={activity} count={count} />
            ))}
          </div>

          {/* Total time for mobile */}
          {totalTravelTime > 0 && (
            <div className="flex items-center text-sm text-gray-600 mb-4 lg:hidden">
              <Clock className="h-4 w-4 mr-1" />
              Total active time: {formatMinutes(totalTravelTime)}
            </div>
          )}

          {/* Timeline */}
          <div className="lg:ml-8">
            {day.travelDetails.map((detail, index) => (
              <TimelinePoint
                key={detail.id || `detail-${index}`}
                detail={detail}
                setOpenImageDialog={setOpenImageDialog}
                setSelectedImage={setSelectedImage}
                handletimelineClick={handletimelineClick}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const BdmTravelReportComponentZM = () => {
  const location = useLocation();
  const { prevFilters, bdmId } = location.state || {};

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [travelData, setTravelData] = useState(null);
  const [bdmInfo, setBdmInfo] = useState([]);
  const [dateRange, setDateRange] = useState(() => {
    const defaultDate = new Date().toISOString().split("T")[0];
    const filterDate = prevFilters;
    return {
      startDate: filterDate || defaultDate,
      endDate: filterDate || defaultDate,
    };
  });

  const [expandedDays, setExpandedDays] = useState({});
  const [isFilterVisible, setIsFilterVisible] = useState(false);

  // Fetch travel data from API
  const fetchTravelData = async () => {
    setLoading(true);
    setError(null);

    try {
      const url = new URL(MONTHLY_BDM_ATTENDANCE_V2);
      url.searchParams.append("bdmId", bdmId);
      url.searchParams.append("startDate", dateRange.startDate);
      url.searchParams.append("endDate", dateRange.endDate);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      // Validate and set defaults
      if (!data || typeof data !== "object") {
        throw new Error("Invalid response format: Expected a data object");
      }

      if (!data.summary) {
        data.summary = {
          dateRange: { start: "N/A", end: "N/A" },
          totalDays: 0,
          totalLocations: 0,
          totalDistance: 0,
          totalTravelForms: 0,
        };
      }

      if (!data.data) {
        data.data = { travelCheckpoints: [] };
      } else if (!data.data.travelCheckpoints) {
        data.data.travelCheckpoints = [];
      }

      setTravelData(data);
      setBdmInfo(data.bdmInfo || []);

      // Auto-expand the first day if there are results
      if (
        data.data.travelCheckpoints &&
        data.data.travelCheckpoints.length > 0
      ) {
        const firstDayDate = data.data.travelCheckpoints[0].date;
        setExpandedDays((prev) => ({ ...prev, [firstDayDate]: true }));
      }
    } catch (err) {
      console.error("Error fetching travel data:", err);
      setError(err.message || "Failed to fetch travel data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch data on component mount and when parameters change
  useEffect(() => {
    fetchTravelData();
  }, []);

  // Handle date range changes
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setDateRange((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Toggle expanded state for a day
  const toggleDayExpanded = (date) => {
    setExpandedDays((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  // Safely get data with defaults
  const dateRangeStart = getSafeData(
    travelData,
    "summary.dateRange.start",
    "N/A"
  );
  const dateRangeEnd = getSafeData(travelData, "summary.dateRange.end", "N/A");
  const totalDays = getSafeData(travelData, "summary.totalDays", 0);
  const totalLocations = getSafeData(travelData, "summary.totalLocations", 0);
  const totalDistance = getSafeData(travelData, "summary.totalDistance", 0);
  const totalTravelForms = getSafeData(
    travelData,
    "summary.totalTravelForms",
    0
  );
  const travelCheckpoints = getSafeData(
    travelData,
    "data.travelCheckpoints",
    []
  );

  // State to handle dialog
  const [openImageDialog, setOpenImageDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Handle image preview
  const handleImageClick = (imgUrl) => {
    setSelectedImage(imgUrl);
    setOpenImageDialog(true);
  };

  const [openTimeline, setopenTimeline] = useState(false);
  const [timelineLogs, setTimelineLogs] = useState([]);
  const [custData, setCustData] = useState([]);

  const handletimelineClick = (row) => {
    setopenTimeline(true);
    setCustData(row);
    axios
      .get(`${GET_TIMELINE}/${row.leadId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((response) => {
        const { leadLogs } = response.data;
        setTimelineLogs(leadLogs);
      })
      .catch((error) => {
        setTimelineLogs([]);
      });
  };

  return (
    <>
      <div className="max-w-7xl mx-auto">
        {/* Header with filters */}
        <div className="mb-4 bg-white rounded-xl shadow-sm p-4 sticky top-0 z-10">
          {/* Header Row */}
          <div className="flex flex-row justify-between md:flex-row md:items-center md:justify-between gap-3 mb-3">
            <div>
              <h1 className="text-lg font-semibold text-gray-900">
                BDM Travel Details
              </h1>
              <p className="text-sm text-gray-600">
                {bdmInfo.bdmName} ({bdmInfo.bdmId})
              </p>
            </div>

            <div>
              <button
                onClick={() => setIsFilterVisible(!isFilterVisible)}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm rounded-md bg-white text-gray-700 hover:bg-gray-50"
              >
                <Filter className="h-4 w-4 mr-2" />
                <span>Filters</span>
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {isFilterVisible && (
            <div className="bg-gray-50 p-3 rounded-md">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Start Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="startDate"
                    value={dateRange.startDate}
                    onChange={handleDateChange}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="endDate"
                    value={dateRange.endDate}
                    onChange={handleDateChange}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Apply Button */}
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      fetchTravelData();
                      setIsFilterVisible(false);
                    }}
                    className="w-full md:w-auto px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Loading and error states */}
        {loading && (
          <div className="bg-white rounded-xl shadow-sm p-8 mb-6 text-center">
            <RefreshCw className="h-8 w-8 text-blue-500 mx-auto mb-4 animate-spin" />
            <p className="text-gray-600">Loading travel data...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl shadow-sm p-6 mb-6">
            <h3 className="font-semibold text-red-800 mb-2">
              Error Loading Data
            </h3>
            <p className="text-red-700">{error}</p>
            <button
              onClick={fetchTravelData}
              className="mt-4 inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md bg-red-100 text-red-800 hover:bg-red-200"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              <span>Try Again</span>
            </button>
          </div>
        )}

        {!loading && !error && travelData && (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                icon={<Calendar className="h-6 w-6" />}
                title="Time Period"
                subtitle={`${dateRangeStart} to ${dateRangeEnd}`}
                value={`${totalDays} day(s)`}
                color="blue"
              />

              <StatCard
                icon={<MapPin className="h-6 w-6" />}
                title="Locations"
                subtitle="Total locations visited"
                value={totalLocations}
                color="red"
              />

              <StatCard
                icon={<Map className="h-6 w-6" />}
                title="Distance"
                subtitle="Total distance traveled"
                value={`${
                  typeof totalDistance === "number"
                    ? totalDistance.toFixed(2)
                    : "0.00"
                } km`}
                color="green"
              />

              <StatCard
                icon={<FileText className="h-6 w-6" />}
                title="Reports"
                subtitle="Travel forms submitted"
                value={totalTravelForms}
                color="purple"
              />
            </div>

            {/* Travel activity legend */}
            {/* <div className="mb-6">
              <h2 className="text-lg font-semibold text-gray-800 mb-3">
                Activity Types
              </h2>
              <div className="flex flex-wrap gap-2 bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                {Object.keys(activityConfig).map((activity) => (
                  <ActivityBadge key={activity} type={activity} />
                ))}
              </div>
            </div> */}

            {/* Daily travel details */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                  <Zap className="h-5 w-5 text-yellow-500 mr-2" />
                  Daily Activity Timeline
                </h2>

                {travelCheckpoints.length > 0 && (
                  <button
                    onClick={() => {
                      // Toggle all - if any are collapsed, expand all. Otherwise collapse all
                      const anyCollapsed = travelCheckpoints.some(
                        (day) => !expandedDays[day.date]
                      );

                      const newExpandedState = {};
                      travelCheckpoints.forEach((day) => {
                        newExpandedState[day.date] = anyCollapsed;
                      });

                      setExpandedDays(newExpandedState);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                  >
                    {travelCheckpoints.some(
                      (day) => !expandedDays[day.date]
                    ) ? (
                      <>
                        Expand All <ChevronDown className="h-4 w-4 ml-1" />
                      </>
                    ) : (
                      <>
                        Collapse All <ChevronRight className="h-4 w-4 ml-1" />
                      </>
                    )}
                  </button>
                )}
              </div>

              {travelCheckpoints.length === 0 ? (
                <div className="bg-white p-8 rounded-xl shadow-sm text-center border border-dashed border-gray-300">
                  <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-1">
                    No Travel Data Available
                  </h3>
                  <p className="text-gray-500">
                    No travel records found for the selected date range
                  </p>
                </div>
              ) : (
                <div>
                  {travelCheckpoints.map((day) => (
                    <DayTimeline
                      key={day.date || "unknown-date"}
                      day={day}
                      isExpanded={!!expandedDays[day.date]}
                      toggleExpand={() => toggleDayExpanded(day.date)}
                      setOpenImageDialog={setOpenImageDialog}
                      setSelectedImage={setSelectedImage}
                      handletimelineClick={handletimelineClick}
                      navigate={navigate}
                      bdmId={bdmInfo.bdmId}
                      bdmName={bdmInfo.bdmName}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <Dialog
        open={openImageDialog}
        onClose={() => setOpenImageDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent
          sx={{ p: 0, position: "relative", backgroundColor: "black" }}
        >
          {/* Close Button */}
          <IconButton
            aria-label="close"
            onClick={() => {
              setOpenImageDialog(false);
              setSelectedImage(null);
            }}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: "#fff",
              zIndex: 1,
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Image */}
          <img
            src={selectedImage}
            alt="Visit"
            style={{ width: "100%", height: "auto", display: "block" }}
          />
        </DialogContent>
      </Dialog>

      <TimelineDialog
        open={openTimeline}
        setOpen={setopenTimeline}
        timelineLogs={timelineLogs}
        custData={custData}
      ></TimelineDialog>
    </>
  );
};

export default BdmTravelReportComponentZM;
